import { RefObject, useEffect, useState } from 'react';
import { setMaxFontSize } from '../utils/maxFontSize';

interface ScaledFontSizeOptions {
  /** Controls if line breaks between words are allowed or not. Default is false. */
  singleLine?: boolean;

  /** Controls if the scaling should happen at all, for conditional cases */
  enabled?: boolean;

  /** Reserved space for other elements */
  reservedSpace?: number;
}

/**
 * Handles automatic text scale-to-fit for an element.
 *
 * @param elementReference A reference to the element.
 * @returns True if the font size was scaled, false otherwise.
 */
export function useScaledFontSize(
  elementReference: RefObject<HTMLElement | null>,
  { singleLine, enabled, reservedSpace }: ScaledFontSizeOptions = {},
) {
  const [usingScaledFontSize, setUsingScaledFontSize] = useState(false);
  useEffect(() => {
    if (enabled === false || elementReference.current === null) {
      return;
    }

    trySetMaxFontSize();

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        trySetMaxFontSize();
      });
    });
    resizeObserver.observe(elementReference.current);

    const rootStyleObserver = new MutationObserver(trySetMaxFontSize);
    rootStyleObserver.observe(document.documentElement, {
      attributeFilter: ['style'],
    });

    const classObserver = new MutationObserver(trySetMaxFontSize);
    classObserver.observe(elementReference.current, {
      attributeFilter: ['class'],
    });

    function trySetMaxFontSize(): void {
      if (!elementReference.current) {
        return;
      }

      // Reset potential font size set by setMaxFontSize
      elementReference.current.style.fontSize = '';

      const newUsingMaxFontSize = setMaxFontSize(
        elementReference.current,
        singleLine ?? false,
        reservedSpace,
      );
      setUsingScaledFontSize(newUsingMaxFontSize);
    }

    return () => {
      resizeObserver.disconnect();
      rootStyleObserver.disconnect();
      classObserver.disconnect();
    };
  }, [elementReference, singleLine, enabled, reservedSpace]);

  return usingScaledFontSize;
}
